/**
 * Where the Sanctum bearer token lives.
 *
 * This sits in the transport layer rather than the auth feature because the HTTP
 * client reads it on every request while the auth feature only writes it twice
 * (login, logout). Putting it here keeps `api -> features/auth -> api` from
 * becoming a cycle.
 *
 * Storage choice follows Remember me:
 * - remember=true → `localStorage` (survives browser restart; longer API expiry)
 * - remember=false → `sessionStorage` (tab-session only)
 *
 * D-008 accepted bearer tokens over cookie sessions, which means the token is
 * readable by any script on the origin; mitigations are the API's expiry,
 * CSP + security headers on admin nginx, DOMPurify on markdown preview HTML,
 * and never rendering unsanitized API HTML through `dangerouslySetInnerHTML`.
 */

const STORAGE_KEY = 'dp.admin.token';
const REMEMBER_KEY = 'dp.admin.remember';

type Listener = () => void;

let cached: string | null | undefined;
const listeners = new Set<Listener>();

function storageFor(remember: boolean): Storage {
  return remember ? window.localStorage : window.sessionStorage;
}

function readRemember(): boolean {
  try {
    return window.localStorage.getItem(REMEMBER_KEY) === '1';
  } catch {
    return false;
  }
}

function read(): string | null {
  if (cached !== undefined) {
    return cached;
  }

  try {
    const remember = readRemember();
    cached =
      storageFor(remember).getItem(STORAGE_KEY) ??
      window.localStorage.getItem(STORAGE_KEY) ??
      window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    // Private browsing modes and hardened profiles can throw on access. An
    // in-memory token still gets the editor through the session.
    cached = null;
  }

  return cached;
}

export const tokenStore = {
  get(): string | null {
    return read();
  },

  set(token: string, options?: { remember?: boolean }): void {
    const remember = options?.remember ?? readRemember();
    cached = token;
    try {
      window.localStorage.setItem(REMEMBER_KEY, remember ? '1' : '0');
      // Clear the other store so a prior remember session cannot leak.
      window.localStorage.removeItem(STORAGE_KEY);
      window.sessionStorage.removeItem(STORAGE_KEY);
      storageFor(remember).setItem(STORAGE_KEY, token);
    } catch {
      /* in-memory only */
    }
    listeners.forEach((listener) => {
      listener();
    });
  },

  clear(): void {
    cached = null;
    try {
      window.localStorage.removeItem(STORAGE_KEY);
      window.sessionStorage.removeItem(STORAGE_KEY);
      window.localStorage.removeItem(REMEMBER_KEY);
    } catch {
      /* in-memory only */
    }
    listeners.forEach((listener) => {
      listener();
    });
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  /** Test seam: drops the memo so a fresh storage value is picked up. */
  reset(): void {
    cached = undefined;
  },
};
